import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS, QUERY_KEYS } from '~shared/api/query-keys';

export interface ClassDetailsForm {
  title: string;
  location?: string | null;
  room?: string | null;
  instructor?: Array<string>;
  maxSize?: number;
  startYear?: number;
  hide?: boolean;
  color?: string;
  session?: string;
  startMonth?: number;
  endYear?: number;
  endMonth?: number;
  group?: Array<string>;
  tags?: Array<string>;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  isPrivateLesson?: boolean;
  slotInterval?: number;
  days?: Array<string>;
  registrationStartDate?: string | null;
  registrationFeeAmount?: number;
  tuitionFee?: number;
  costumeFee?: number;
  tuitionBillingCycle?: string;
  billingFrequencyDays?: number;
  hourlyTuitionBillingId: string | null;
  classCountTuitionBillingId?: string | null;
  billingDay?: number;
  prorateTuition?: boolean;
  prorateOneTimePackage?: boolean;
  description?: string;
  policyGroup?: Array<string>;
  displayOnWebsite?: boolean;
  allowPortalEnrollment?: boolean;
  allowOnlineRegistration?: boolean;
  allowTrialEnrollment?: boolean;
  includeInPublicSchedule?: boolean;
  acceptsMakeups?: boolean;
  availability?: Array<{
    day: string;
    startTime: string;
    endTime: string;
  }>;
  timeConfig: string;
}

export const updateClassAPI = async (
  classId: string,
  file: File | string | null,
  fileType: 'file' | 'url',
  classDetails: ClassDetailsForm,
) => {
  const formData = new FormData();

  if (fileType === 'file' && file instanceof File) {
    formData.append('file', file);
  } else if (fileType === 'url' && typeof file === 'string') {
    formData.append('fileUrl', file);
  }

  formData.append('fileType', fileType);

  // Add stringified class details as payload
  formData.append('payload', JSON.stringify(classDetails));

  // Do not set Content-Type: let the client set multipart/form-data with the correct boundary.
  // Setting it manually to 'multipart/form-data' without boundary breaks parsing on the server.
  const resp = await baseAPI.patch<unknown>(`/enrollments/${classId}`, formData);
  return resp.data;
};

export const invalidateAfterClassUpdate = (classId: string) => {
  void queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.CLASSES(),
  });
  void queryClient.invalidateQueries({
    queryKey: [KEYS.CLASSES, classId],
  });
  // Editing a class changes its seat limit (maxSize), which the dashboard's
  // capacity metrics are derived from. Invalidate them so the ≥90% badge and
  // the Near Capacity / Low Enrollment widgets recalculate and stay in sync
  // for the selected session (mirrors the enroll/drop/transfer flows). The
  // mutation isn't session-scoped, so invalidate across all sessions.
  void queryClient.invalidateQueries({
    queryKey: [KEYS.NINETY_PERCENT_FULL_CLASSES],
  });
  void queryClient.invalidateQueries({
    queryKey: [KEYS.CLASS_PERFORMANCE_NEAR_CAPACITY],
  });
  void queryClient.invalidateQueries({
    queryKey: [KEYS.CLASS_PERFORMANCE_LOW_ENROLLMENT],
  });
};

export const useUpdateClassMutation = () => {
  return useMutation({
    mutationFn: async ({
      classId,
      file,
      fileType,
      classDetails,
    }: {
      classId: string;
      file: File | string | null;
      fileType: 'file' | 'url';
      classDetails: ClassDetailsForm;
    }) => {
      const response = await updateClassAPI(
        classId,
        file,
        fileType,
        classDetails,
      );
      return response;
    },
    onSuccess: (_, params) => {
      invalidateAfterClassUpdate(params.classId);
    },
  });
};
